/**
 * Duct System Tool
 * 
 * Manages duct systems - grouping ducts by system type, calculating total flow/pressure.
 */

import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';

export type DuctSystemType = 'Supply Air' | 'Return Air' | 'Exhaust Air' | 'Outside Air' | 'Mixed Air';

export interface DuctSystem {
    id: string;
    name: string;
    type: DuctSystemType;
    memberIds: string[]; // Duct element IDs
    equipmentId?: string; // Connected AHU/Equipment

    // Calculated values
    totalFlow: number; // L/s
    totalPressure: number; // Pa
    criticalPath: string[]; // IDs of ducts in critical path
}

export class DuctSystemTool extends BaseTool {
    id = 'duct_system';
    name = 'Duct System';
    cursor = 'default';

    private systems: Map<string, DuctSystem> = new Map();

    /**
     * Create a new duct system
     */
    public createSystem(name: string, type: DuctSystemType, equipmentId?: string): DuctSystem {
        const system: DuctSystem = {
            id: uuidv4(),
            name,
            type,
            memberIds: [],
            equipmentId,
            totalFlow: 0,
            totalPressure: 0,
            criticalPath: []
        };

        this.systems.set(system.id, system);
        console.log(`Created Duct System: ${name} (${type})`);
        return system;
    }

    /**
     * Add a duct to a system
     */
    public addToSystem(systemId: string, ductId: string): void {
        const system = this.systems.get(systemId);
        if (!system) return;

        if (!system.memberIds.includes(ductId)) {
            system.memberIds.push(ductId);
        }
    }

    /**
     * Remove a duct from its system
     */
    public removeFromSystem(systemId: string, ductId: string): void {
        const system = this.systems.get(systemId);
        if (!system) return;

        system.memberIds = system.memberIds.filter(id => id !== ductId);
    }

    /**
     * Calculate system totals
     */
    public calculateSystem(systemId: string): DuctSystem | null {
        if (!this.context) return null;

        const system = this.systems.get(systemId);
        if (!system) return null;

        let totalFlow = 0;
        let totalPressure = 0;
        const maxPressurePath: string[] = [];
        let maxPressure = 0;

        // Iterate through members
        for (const ductId of system.memberIds) {
            const duct = this.context.bimStore.project.elements.get(ductId) as any;
            if (!duct) continue;

            // Sum flows (for terminals)
            const flow = parseFloat(duct.parameters?.['Flow'] || '0');
            totalFlow += flow;

            // Track pressure drops
            const pressure = parseFloat(duct.pressure?.totalDrop || duct.parameters?.['Pressure Drop'] || '0');
            totalPressure += pressure;

            // Simple critical path (highest pressure drop)
            if (pressure > maxPressure) {
                maxPressure = pressure;
                maxPressurePath.push(ductId);
            }
        }

        system.totalFlow = totalFlow;
        system.totalPressure = totalPressure;
        system.criticalPath = maxPressurePath;

        console.log(`System ${system.name}: ${totalFlow} L/s, ${totalPressure.toFixed(1)} Pa`);
        return system;
    }

    /**
     * Get all systems
     */
    public getAllSystems(): DuctSystem[] {
        return Array.from(this.systems.values());
    }

    /**
     * Get system by ID
     */
    public getSystem(id: string): DuctSystem | undefined {
        return this.systems.get(id);
    }

    /**
     * Auto-create systems from existing ducts
     */
    public autoCreateSystems(): void {
        if (!this.context) return;

        const elements = Array.from(this.context.bimStore.project.elements.values());
        const ducts = elements.filter((e: any) => e.category === 'Ducts');

        // Group by system type
        const grouped: Map<string, string[]> = new Map();

        for (const duct of ducts) {
            const systemType = (duct as any).systemType || (duct as any).parameters?.['System Type'] || 'Supply Air';
            if (!grouped.has(systemType)) {
                grouped.set(systemType, []);
            }
            grouped.get(systemType)!.push(duct.id);
        }

        // Create systems
        for (const [type, ductIds] of grouped) {
            const system = this.createSystem(`${type} System`, type as DuctSystemType);
            ductIds.forEach(id => this.addToSystem(system.id, id));
            this.calculateSystem(system.id);
        }
    }

    // Tool interface
    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // Opens System Browser dialog in real app
        console.log('Duct System Browser would open here');
        console.log('Systems:', this.getAllSystems().map(s => `${s.name}: ${s.memberIds.length} members`));
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }
}
